import React from 'react';
import { getYrkesgruppColor } from '../../../utils/yrkesgruppColors';
import { medArvdYrkesgrupp } from '../../../utils/arbetsbeskrivningRum';

/**
 * Formatera valuta till svensk standard
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0 kr';
  }
  return Math.round(value).toLocaleString('sv-SE') + ' kr';
};

/**
 * WorkDescriptionSection - Gemensam komponent för arbetsbeskrivning
 *
 * Används av både PU och Offert för att visa strukturerad arbetsbeskrivning.
 *
 * Visar:
 * - Huvudrubriker (stora, med border) - med valfri kostnad
 * - Underrubriker (medelstora, fetstil)
 * - Punktlistor
 * - Tomma rader för struktur
 *
 * @param {Array} arbetsbeskrivning - Array med arbetsbeskrivningsrader
 * @param {boolean} isCollapsed - Om sektionen är ihopfälld
 * @param {Function} onToggle - Callback för att toggla ihopfällning
 * @param {string} title - Titel för sektionen
 * @param {string} className - CSS-klasser
 * @param {Object} sectionCosts - Objekt med kostnad per sektion { "Badrum": 225414, "Kök": 161425 }
 * @param {Object} correctedSectionCosts - Objekt med KORRIGERADE kostnader (inkl. byggarens ändringar)
 * @param {boolean} showSectionCosts - Om sektionskostnader ska visas
 * @param {string} priceLabel - Label för kostnaden (t.ex. "Kundpris inkl. moms" eller "Byggarens budget")
 * @param {boolean} showYrkesgruppColors - Om yrkesgrupp-färgkodning ska visas
 */
const WorkDescriptionSection = ({
  arbetsbeskrivning,
  isCollapsed,
  onToggle,
  title = "📝 Arbetsbeskrivning",
  className = "border border-gray-200 rounded-lg overflow-hidden mb-4",
  sectionCosts = {},
  correctedSectionCosts = {},
  optionSectionCosts = {},
  showSectionCosts = false,
  showRowPrices = false,
  priceLabel = "Kundpris inkl. moms",
  showYrkesgruppColors = false
}) => {
  // Visa inte om ingen arbetsbeskrivning finns
  if (!arbetsbeskrivning || arbetsbeskrivning.length === 0) return null;

  /**
   * Hitta kostnaden för en huvudrubrik
   * Använder correctedSectionCosts (med byggarens ändringar) om tillgängligt,
   * annars faller tillbaka på sectionCosts (originalvärden)
   */
  const getSectionCost = (headerText) => {
    if (!showSectionCosts) {
      return null;
    }

    // Välj källa: korrigerade kostnader om de finns, annars original
    const hasCorrectedCosts = correctedSectionCosts && Object.keys(correctedSectionCosts).length > 0;
    const costsSource = hasCorrectedCosts ? correctedSectionCosts : sectionCosts;

    if (!costsSource || Object.keys(costsSource).length === 0) {
      return null;
    }

    // Exakt matchning först
    if (costsSource[headerText] !== undefined) {
      return costsSource[headerText];
    }

    // Försök hitta delvis matchning (t.ex. "För- & efterarbeten" vs "För & efterarbeten")
    const normalizedHeader = headerText.toLowerCase().replace(/[^a-zåäö0-9]/g, '');
    for (const [key, value] of Object.entries(costsSource)) {
      const normalizedKey = key.toLowerCase().replace(/[^a-zåäö0-9]/g, '');
      if (normalizedKey === normalizedHeader || normalizedKey.includes(normalizedHeader) || normalizedHeader.includes(normalizedKey)) {
        return value;
      }
    }

    return null;
  };

  // Hämta option-kostnad för en huvudrubrik (kundpris inkl arvode + sop + moms)
  const getOptionCost = (headerText) => {
    if (!optionSectionCosts || Object.keys(optionSectionCosts).length === 0) return null;
    if (optionSectionCosts[headerText] !== undefined) return optionSectionCosts[headerText];
    const normalizedHeader = headerText.toLowerCase().replace(/[^a-zåäö0-9]/g, '');
    for (const [key, value] of Object.entries(optionSectionCosts)) {
      const normalizedKey = key.toLowerCase().replace(/[^a-zåäö0-9]/g, '');
      if (normalizedKey === normalizedHeader) return value;
    }
    return null;
  };

  return (
    <div className={className}>
      {/* Header */}
      <div 
        className="bg-gray-100 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors flex items-center justify-between"
        onClick={onToggle}
      >
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <div className="text-gray-600 transition-transform duration-200">
          {isCollapsed ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>
      
      {/* Innehåll */}
      {!isCollapsed && (
        <div className="p-4 bg-white">
          <div className="space-y-1">
            {medArvdYrkesgrupp(arbetsbeskrivning).map((item, index) => {
              const optionCost = item.isMainHeader ? getOptionCost(item.text) : null;
              const isOptionHeader = optionCost !== null;
              // item.isOption utan optionCost: en option utan belopp. Den får inte
              // falla igenom till getSectionCost, vars delvisa matchning annars ger
              // "Kök option" priset för "Kök".
              const sectionCost = item.isMainHeader && !isOptionHeader && !item.isOption ? getSectionCost(item.text) : null;
              // Hämta färg för yrkesgrupp (endast för content-rader med giltig yrkesgrupp)
              const yg = item.yrkesgrupp;
              const hasValidYrkesgrupp = yg && yg !== 0 && yg !== '0';
              const yrkesgruppColor = showYrkesgruppColors && !item.isMainHeader && !item.isSubHeader && !item.isEmpty && hasValidYrkesgrupp
                ? getYrkesgruppColor(yg)
                : null;

              const hasRowPrice = showRowPrices && !item.isMainHeader && !item.isSubHeader && item.rowCost > 0;
              return (
                <div key={index}>
                  {item.isEmpty ? (
                    <div className="h-3"></div>
                  ) : (
                    <div
                      className={`${
                        item.isMainHeader
                          ? 'font-bold text-gray-800 text-lg mt-6 mb-2 pb-2 border-b border-gray-300 flex items-center justify-between'
                          : item.isSubHeader && !item.text?.startsWith('•')
                          ? 'font-bold text-gray-700 text-base mt-4 mb-1'
                          : `text-gray-600 leading-relaxed text-sm ml-4 ${hasRowPrice ? 'flex items-baseline justify-between gap-3' : ''}`
                      } ${yrkesgruppColor ? 'border-l-4 pl-2 -ml-2' : ''}`}
                      style={yrkesgruppColor ? { borderLeftColor: yrkesgruppColor.hex } : {}}
                    >
                      <span>{item.text}</span>
                      {/* Kundpris per rad (för content-rader när BK aktiverat) */}
                      {showRowPrices && !item.isMainHeader && !item.isSubHeader && item.rowCost > 0 && (
                        <span
                          className="text-xs text-gray-500 ml-3 whitespace-nowrap tabular-nums"
                          title={`${priceLabel} för denna rad`}
                        >
                          {formatCurrency(item.rowCost)}
                        </span>
                      )}
                      {/* Option-kostnad (lila badge till höger) */}
                      {isOptionHeader && (
                        <span
                          className="text-xs font-semibold text-purple-800 ml-3 px-2 py-0.5 rounded bg-purple-100 border border-purple-300 text-right"
                          title="Denna sektion är en option — ej inräknat i total"
                        >
                          OPTION, ingår ej i totalen — {formatCurrency(optionCost)}
                        </span>
                      )}
                      {/* Vanlig sektionskostnad (icke-option) */}
                      {sectionCost !== null && (
                        <span
                          className="text-sm font-normal text-gray-400 ml-3 flex items-center gap-1"
                          title={`${priceLabel} för ${item.text}`}
                        >
                          <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formatCurrency(sectionCost)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        </div>
      )}
    </div>
  );
};

export default WorkDescriptionSection;

